@if (!loading() && pagedData().length === 0) {
  <div role="status" class="flex justify-center items-center dark:border-gray-700 p-4">
      <span role="status" class="text-base text-gray-700 dark:text-gray-300">
        {{ emptyDataMessage() }}
      </span>
  </div>
} @else {
  <div class="overflow-x-auto rounded-t-xl" [class.custom-table-top-border]="visibleHeader()">
    <table class="custom-table">
      <!-- Table Header -->
      <thead class="text-slate-700 dark:text-gray-100"
             [class.custom-table-background]="visibleHeader()">
      <tr>
        @for (col of columns(); track $index) {
          <th (click)="sortByColumn(col)" [id]="col.columnId"
              class="min-w-[150px] font-normal whitespace-nowrap px-6 py-3" [class.col-sortable]="col.sortable"
              [ngStyle]="getColumnStyles(col)" [class]="customHeaderClasses()">
            @if (isTemplateRef(col.header)) {
              <ng-container *ngTemplateOutlet="col.header"/>
            } @else {
              <div>
                {{ col.header }}
                @if (sortParams()?.columnId === col.columnId) {
                  <span>{{ sortParams()?.direction === 'asc' ? '↑' : '↓' }}</span>
                }
              </div>
            }
          </th>
        }
        @if (actions().length > 0) {
          <th class="font-normal text-center px-1 py-1" [class]="customHeaderClasses()" [attr.colspan]="actions().length">
            Actions
          </th>
        }
      </tr>
      </thead>
      <!-- Table Body -->
      <tbody class="custom-table-background">
        @if (!loading()) {
          @for (row of pagedData(); track row[primaryDataKey()]) {
            <tr class="group"
                [ngClass]="{
                  'hover:bg-gray-200 dark:hover:bg-gray-700': clickableRows(),
                  'bg-[#fffcef] dark:bg-gray-700 hover:bg-[#fff8e0] dark:hover:bg-gray-600': selectedRowId() === row[primaryDataKey()]
                }"
                [class.cursor-pointer]="selectableRows() || clickableRows()"
                (click)="onRowClick(row[primaryDataKey()], row)">
              <!-- Data Cells -->
              @for (col of columns(); track $index) {
                <td class="max-h-[200px] font-normal px-6 py-4" [ngStyle]="getColumnStyles(col)"
                    [class]="customRowClasses()">
                  <!-- Displaying cell content -->
                  <div class="max-w-full overflow-hidden" [class.inline-block]="col.type === TableColumnType.Tag">
                    @if (hasValue(row[col.columnId])) {
                      @switch (col.type) {
                        @case (TableColumnType.Text) {
                          {{ row[col.columnId] }}
                        }
                        @case (TableColumnType.Integer) {
                          {{ row[col.columnId] | decimals:0 }}
                        }
                        @case (TableColumnType.Float) {
                          {{ row[col.columnId] | decimals:2 }}
                        }
                        @case (TableColumnType.Boolean) {
                          @if (row[col.columnId]) {
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                 fill="none">
                              <g clip-path="url(#clip0_992_1878)">
                                <path
                                  d="M9.00016 16.1701L4.83016 12.0001L3.41016 13.4101L9.00016 19.0001L21.0002 7.00009L19.5902 5.59009L9.00016 16.1701Z"
                                  fill="#434343"/>
                              </g>
                              <defs>
                                <clipPath id="clip0_992_1878">
                                  <rect width="24" height="24" fill="white"/>
                                </clipPath>
                              </defs>
                            </svg>
                          } @else {
                            --
                          }
                        }
                        @case (TableColumnType.Currency) {
                          <!-- Currency (formatted with two decimals) -->
                          {{ row[col.columnId] | currency:'USD':'symbol':'1.2-2' }}
                        }
                        @case (TableColumnType.Tag) {
                          <div class="overflow-hidden">
                            <tags
                              [tags]="col.multiple ? row[col.columnId] : (row[col.columnId] ? [row[col.columnId]] : [])"/>
                          </div>
                        }
                        @case (TableColumnType.Selector) {
                          <div class="overflow-hidden">
                            @if (row[col.columnId]) {
                              @if (col.multiple) {
                                @if (isArray(row[col.columnId]) && row[col.columnId].length > 0) {
                                  <div class="flex flex-col gap-1">
                                    @for (item of $any(row[col.columnId]); track item.user_id || $index) {
                                      <div class="text-xs">{{ item.name }}</div>
                                    }
                                  </div>
                                } @else {
                                  <div class="text-xs text-gray-400">Select person</div>
                                }
                              } @else {
                                @if (isArray(row[col.columnId]) && row[col.columnId].length > 0) {
                                  <div class="text-xs">{{ row[col.columnId][0].name }}</div>
                                } @else {
                                  <div class="text-xs">{{ row[col.columnId].name }}</div>
                                }
                              }
                            } @else {
                              <div class="text-xs text-gray-400">Select person</div>
                            }
                          </div>
                        }
                        @case (TableColumnType.Link) {
                          <!-- Link (object with label and url, or url string) -->
                          @if (row[col.columnId]) {
                            <a [href]="row[col.columnId].url || row[col.columnId]" target="_blank"
                               class="hover:underline" stopClickPropagation>
                              {{ row[col.columnId].label || row[col.columnId] }}
                            </a>
                          }
                        }
                        @case (TableColumnType.Template) {
                          <!-- Custom Template -->
                          <ng-container *ngTemplateOutlet="col.template || null; context: { $implicit: row, row: row }"/>
                        }
                        @default {
                          {{ row[col.columnId] }}
                        }
                      }
                    } @else {
                      --
                    }
                  </div>
                </td>
              }
              <!-- Actions Cell -->
              @for (action of actions(); track $index) {
                <td class="px-2">
                  <button [style.color]="getActionStringValue(action.textColor, row)"
                          [style.background-color]="getActionStringValue(action.backgroundColor, row)"
                          [disabled]="getActionBooleanValue(action.disable, row)"
                          [class.hidden]="getActionBooleanValue(action.hide, row)"
                          class="row-action" stopClickPropagation
                          (click)="onActionClick(action, row)">
                    @if (action.icon) {
                      <ng-container *ngTemplateOutlet="action.icon; context: { $implicit: row, row: row }"/>
                    }
                    @let displayName = getActionStringValue(action.displayName, row);
                    @if (displayName) {
                      <span>{{ displayName }}</span>
                    }
                  </button>
                </td>
              }
            </tr>
          }
        }
      </tbody>
    </table>

    @if (loading()) {
      <loading-pulse [pulseCount]="pulseLoadingCount()"/>
    }
  </div>
  @if (usePagination()) {
    <div class="flex gap-1 items-center text-sm bg-white dark:bg-gray-800 rounded-b-xl px-4 py-1">
      <div class="flex-1 flex gap-1 justify-start items-center">
          <span class="text-gray-500 dark:text-gray-300 font-bold">
            Showing {{ startIndex() }} - {{ endIndex() }} of {{ totalItems() }}
          </span>
        @if (loading()) {
          <loading-spinner size="24px"/>
        }
      </div>
      <div class="flex-1 flex gap-2 justify-center items-center">
        <button (click)="prevPage()" [disabled]="currentPage() <= 1" class="custom-table-page-btn"
                title="Previous page" aria-label="Prev Page">
          <svg width="5" height="8" fill="none" class="scale-x-[-1]">
            <path d="M1 1l3 3-3 3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"
                  stroke-linejoin="round"/>
          </svg>
        </button>
        <span class="font-bold dark:text-gray-100">{{ currentPage() }} of {{ totalPages() }}</span>
        <button (click)="nextPage()" [disabled]="currentPage() >= totalPages()" class="custom-table-page-btn"
                title="Next page" aria-label="Next Page">
          <svg width="5" height="8" fill="none">
            <path d="M1 1l3 3-3 3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"
                  stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
      <div class="flex-1 flex gap-1 justify-end items-center text-gray-500 dark:text-gray-300 font-bold">
        <span>Rows per page</span>
        <select [(ngModel)]="pageSize" aria-label="Items per page select"
                class="border-0 cursor-pointer focus:outline-none focus:ring-0 bg-white dark:bg-gray-800 dark:text-gray-100">
          @for (size of fullPageSizeOptions(); track $index) {
            <option [ngValue]="size">{{ size }}</option>
          }
        </select>
      </div>
    </div>
  }
}
